import { Box, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { ShieldAlertIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useAuth } from '~/contexts/AuthContext'

export const Route = createFileRoute('/_app/admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const { user, isLoading, hasMinRole } = useAuth()
  const navigate = useNavigate()

  // Check if user has at least operator role (operators and admins can access admin section)
  const hasAccess = hasMinRole('operator')

  // Redirect users without access after a short delay to show the message
  useEffect(() => {
    if (!isLoading && !hasAccess) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isLoading, hasAccess, navigate])

  // Show loading while checking auth
  if (isLoading) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.500">Checking permissions...</Text>
        </VStack>
      </Flex>
    )
  }

  // Show access denied for users without operator+ role
  if (!hasAccess) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="red.100" _dark={{ bg: 'red.900' }}>
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="red.500">
            Access Denied
          </Heading>
          <Text color="gray.500" maxW="400px">
            You don't have permission to access this page. Only users with the{' '}
            <strong>operator</strong> or <strong>admin</strong> role can view admin pages.
          </Text>
          <Text fontSize="sm" color="gray.400">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Render admin content for operators and admins
  return <Outlet />
}
